/**
 * BulkAssignmentDialog
 *
 * Size-per-row layout:
 *  - One row per active size (rows with 0 cut pieces are hidden)
 *  - Columns: Size | Cut | Unassigned | Worker (Autocomplete) | Qty | clear
 *  - Unassigned count updates live as user types Qty
 *  - Save → POST /worker-assignments/bulk (one DB record per filled row)
 *
 * Step 1: Design / Garment Part + Job Type + Issue Date + optional Rate + Notes
 * Step 2: Per-size worker + qty grid (auto-rendered after design selected)
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  InputAdornment,
  Stack,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import api from '../services/api';
import { AvailableDesign, ActiveWorker } from '../types/workerAssignment';

// ─── types ────────────────────────────────────────────────────────────────────
const ALL_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'] as const;
type Size = typeof ALL_SIZES[number];

interface SizeCapacity {
  cut: number;
  outstanding: number;
  goodReceived: number;
  closedShortfall: number;
  available: number;
}

/** One row = one size. Worker + qty chosen for that size. */
interface SizeRow {
  size: Size;
  workerId: string; // worker.id (numeric PK as string), '' = unset
  qty: string;      // raw text input
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  availableDesigns: AvailableDesign[];
  availableWorkers: ActiveWorker[];
}

export default function BulkAssignmentDialog({
  open,
  onClose,
  onSaved,
  availableDesigns,
  availableWorkers,
}: Props) {
  // ── Step 1 ─────────────────────────────────────────────────────────────────
  const [selectedDesign, setSelectedDesign] = useState<AvailableDesign | null>(null);
  const [jobType, setJobType] = useState<'STITCHING' | 'PRESS'>('STITCHING');
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
  const [stitchingRate, setStitchingRate] = useState('');
  const [notes, setNotes] = useState('');

  // ── Capacity ────────────────────────────────────────────────────────────────
  const [capacity, setCapacity] = useState<Record<string, SizeCapacity>>({});
  const [capacityLoading, setCapacityLoading] = useState(false);

  // ── Size rows (one per active size) ────────────────────────────────────────
  const [sizeRows, setSizeRows] = useState<SizeRow[]>([]);

  // ── Submit ──────────────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitResult, setSubmitResult] = useState<{ created: number; errors: any[] } | null>(null);

  // ── Derived ─────────────────────────────────────────────────────────────────
  const activeSizes = ALL_SIZES.filter(s => (capacity[s]?.cut ?? 0) > 0);

  // Unassigned remaining for a size = available - qty entered in this row
  const remaining = (size: Size): number => {
    const avail = capacity[size]?.available ?? 0;
    const row = sizeRows.find(r => r.size === size);
    const entered = parseInt(row?.qty || '0') || 0;
    return Math.max(0, avail - entered);
  };

  // ── Fetch capacity ──────────────────────────────────────────────────────────
  const fetchCapacity = useCallback(async (design: AvailableDesign, jt: string) => {
    setCapacityLoading(true);
    setCapacity({});
    setSizeRows([]);
    try {
      const res = await api.get('/worker-assignments/capacity', {
        params: { design_no: design.design_no, garment_part: design.garment_part, jobType: jt },
      });
      const cap: Record<string, SizeCapacity> = res.data.capacity || {};
      setCapacity(cap);
      // Build one row per size that has cut pieces
      const rows: SizeRow[] = ALL_SIZES
        .filter(s => (cap[s]?.cut ?? 0) > 0)
        .map(s => ({ size: s, workerId: '', qty: '' }));
      setSizeRows(rows);
    } catch {
      setCapacity({});
      setSizeRows([]);
    } finally {
      setCapacityLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedDesign) {
      fetchCapacity(selectedDesign, jobType);
    } else {
      setCapacity({});
      setSizeRows([]);
    }
  }, [selectedDesign, jobType, fetchCapacity]);

  // ── Reset on open ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      setSelectedDesign(null);
      setJobType('STITCHING');
      setIssueDate(new Date().toISOString().split('T')[0]);
      setStitchingRate('');
      setNotes('');
      setSizeRows([]);
      setCapacity({});
      setSubmitError(null);
      setSubmitResult(null);
    }
  }, [open]);

  // ── Row updaters ────────────────────────────────────────────────────────────
  const setRowWorker = (size: Size, workerId: string) =>
    setSizeRows(prev => prev.map(r => r.size === size ? { ...r, workerId } : r));

  const setRowQty = (size: Size, qty: string) =>
    setSizeRows(prev => prev.map(r => r.size === size ? { ...r, qty } : r));

  const clearRow = (size: Size) =>
    setSizeRows(prev => prev.map(r => r.size === size ? { ...r, workerId: '', qty: '' } : r));

  // ── Validation ──────────────────────────────────────────────────────────────
  // A row is "active" if the user filled in either worker or qty
  const filledRows = sizeRows.filter(r => r.workerId || (parseInt(r.qty) > 0));

  const rowError = (row: SizeRow): string | null => {
    const qty = parseInt(row.qty) || 0;
    if ((row.workerId && !qty) || (!row.workerId && qty > 0)) return 'Both worker and qty required';
    if (qty > (capacity[row.size]?.available ?? 0)) return `Exceeds available (${capacity[row.size]?.available ?? 0})`;
    return null;
  };

  const hasErrors = filledRows.some(r => rowError(r) !== null);

  const canSubmit =
    !!selectedDesign &&
    filledRows.length > 0 &&
    !hasErrors;

  const totalPcs = sizeRows.reduce((s, r) => s + (parseInt(r.qty) || 0), 0);

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!selectedDesign || !canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitResult(null);

    const items = filledRows
      .filter(r => r.workerId && parseInt(r.qty) > 0)
      .map(r => ({
        workerId: r.workerId,
        design_no: selectedDesign.design_no,
        garment_part: selectedDesign.garment_part,
        size: r.size,
        quantity: parseInt(r.qty),
      }));

    try {
      const res = await api.post('/worker-assignments/bulk', {
        assignments: items,
        issueDate,
        jobType,
        stitching_rate: stitchingRate || undefined,
        notes: notes || undefined,
      });
      const result = res.data as { created: number; errors: any[] };
      setSubmitResult(result);
      if (result.errors.length === 0) {
        onSaved();
        onClose();
      }
    } catch (err: any) {
      setSubmitError(err.response?.data?.error || 'Failed to create assignments');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Chip colour: green → yellow → red based on remaining fraction ───────────
  const remainingChipColor = (size: Size) => {
    const avail = capacity[size]?.available ?? 0;
    if (avail === 0) return 'default' as const;
    const rem = remaining(size);
    if (rem === avail) return 'success' as const;        // nothing entered yet
    if (rem > 0) return 'warning' as const;              // partially filled
    return 'error' as const;                             // fully allocated
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>Bulk Worker Assignment</Typography>
        <Typography variant="body2" color="text.secondary">
          Select a design, then assign a worker and quantity for each size.
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2.5 } }}>

        {/* ── Step 1: Design + Job Details ──────────────────────────────── */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2.5, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1.5 }}>
            1. Design &amp; Job Details
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>

            {/* Design picker */}
            <Autocomplete
              sx={{ flex: '2 1 220px' }}
              options={availableDesigns}
              getOptionLabel={o => `${o.design_no} — ${o.garment_part}`}
              value={selectedDesign}
              onChange={(_, v) => setSelectedDesign(v)}
              renderInput={params => (
                <TextField {...params} label="Design / Garment Part" required size="small" />
              )}
              renderOption={(props, option) => (
                <Box component="li" sx={{ display: 'flex', alignItems: 'center', gap: 1 }} {...props}>
                  {option.image_url && (
                    <Box
                      component="img"
                      src={option.image_url}
                      sx={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                    />
                  )}
                  <Box>
                    <Typography variant="body2">{option.design_no} — {option.garment_part}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Batch {option.batch_number} · {option.total_pieces} pcs
                    </Typography>
                  </Box>
                </Box>
              )}
            />

            <FormControl size="small" sx={{ flex: '1 1 130px' }}>
              <InputLabel>Job Type</InputLabel>
              <Select
                value={jobType}
                label="Job Type"
                onChange={e => setJobType(e.target.value as 'STITCHING' | 'PRESS')}
              >
                <MenuItem value="STITCHING">Stitching</MenuItem>
                <MenuItem value="PRESS">Press</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Issue Date"
              type="date"
              value={issueDate}
              onChange={e => setIssueDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: '1 1 140px' }}
            />

            <TextField
              size="small"
              label="Rate (optional)"
              type="number"
              value={stitchingRate}
              onChange={e => setStitchingRate(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              sx={{ flex: '1 1 110px' }}
            />

            <TextField
              size="small"
              label="Notes (optional)"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              sx={{ flex: '2 1 180px' }}
            />
          </Stack>
        </Paper>

        {/* ── Step 2: Size × Worker grid ─────────────────────────────────── */}
        {selectedDesign && (
          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            {/* Header */}
            <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" fontWeight={600} color="primary">
                2. Assign Worker per Size
              </Typography>
              {capacityLoading && <CircularProgress size={16} />}
              <Tooltip title="Unassigned = cut pieces minus already-outstanding and received. Updates live as you type." placement="right">
                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', ml: 'auto' }} />
              </Tooltip>
            </Box>

            {!capacityLoading && activeSizes.length === 0 && (
              <Alert severity="warning" sx={{ mx: 2, mb: 2 }}>
                No available capacity — all sizes for this design are fully assigned.
              </Alert>
            )}

            {!capacityLoading && activeSizes.length > 0 && (
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ width: 90, fontWeight: 700, fontSize: '0.75rem' }}>SIZE</TableCell>
                    <TableCell sx={{ width: 90, fontWeight: 700, fontSize: '0.75rem' }} align="center">CUT</TableCell>
                    <TableCell sx={{ width: 120, fontWeight: 700, fontSize: '0.75rem' }} align="center">
                      UNASSIGNED
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>WORKER</TableCell>
                    <TableCell sx={{ width: 100, fontWeight: 700, fontSize: '0.75rem' }} align="center">QTY</TableCell>
                    <TableCell sx={{ width: 40 }} />
                  </TableRow>
                </TableHead>

                <TableBody>
                  {sizeRows.map(row => {
                    const cap = capacity[row.size];
                    const isFullyAssigned = (cap?.available ?? 0) === 0;
                    const rem = remaining(row.size);
                    const err = row.workerId || parseInt(row.qty) > 0 ? rowError(row) : null;
                    const selectedWorker = availableWorkers.find(w => w.id.toString() === row.workerId) || null;
                    const qtyNum = parseInt(row.qty) || 0;

                    // ── Fully assigned / no capacity row (read-only, greyed) ──
                    if (isFullyAssigned) {
                      return (
                        <TableRow
                          key={row.size}
                          sx={{ '&:last-child td': { border: 0 }, opacity: 0.45, bgcolor: 'grey.50' }}
                        >
                          <TableCell>
                            <Chip
                              label={row.size}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 700, minWidth: 52, color: 'text.disabled', borderColor: 'divider' }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" color="text.disabled">{cap?.cut ?? 0}</Typography>
                            <Typography variant="caption" color="text.disabled" display="block">rcvd {cap?.goodReceived ?? 0}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip label="Fully assigned" size="small" variant="outlined" sx={{ fontSize: '0.68rem', height: 20, color: 'text.disabled', borderColor: 'divider' }} />
                            {cap?.outstanding ? (
                              <Typography variant="caption" color="text.disabled" display="block">out {cap.outstanding}</Typography>
                            ) : null}
                          </TableCell>
                          <TableCell colSpan={3}>
                            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                              No pieces available to assign
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    }

                    // ── Assignable row ────────────────────────────────────────
                    return (
                      <TableRow
                        key={row.size}
                        sx={{
                          '&:last-child td': { border: 0 },
                          bgcolor: err ? 'error.50' : 'inherit',
                        }}
                      >
                        {/* Size badge */}
                        <TableCell>
                          <Chip
                            label={row.size}
                            size="small"
                            color={qtyNum > 0 && !err ? 'primary' : 'default'}
                            variant={qtyNum > 0 ? 'filled' : 'outlined'}
                            sx={{ fontWeight: 700, minWidth: 52 }}
                          />
                        </TableCell>

                        {/* Cut count */}
                        <TableCell align="center">
                          <Typography variant="body2" color="text.secondary">
                            {cap?.cut ?? 0}
                          </Typography>
                          <Typography variant="caption" color="text.disabled" display="block">
                            rcvd {cap?.goodReceived ?? 0}
                          </Typography>
                        </TableCell>

                        {/* Unassigned remaining chip */}
                        <TableCell align="center">
                          <Chip
                            label={`${rem} left`}
                            size="small"
                            color={remainingChipColor(row.size)}
                            variant="outlined"
                            sx={{ fontSize: '0.72rem', height: 22 }}
                          />
                          {cap?.outstanding ? (
                            <Typography variant="caption" color="text.disabled" display="block">
                              out {cap.outstanding}
                            </Typography>
                          ) : null}
                        </TableCell>

                        {/* Worker autocomplete */}
                        <TableCell>
                          <Autocomplete
                            size="small"
                            options={availableWorkers}
                            getOptionLabel={w => `${w.name} (${w.worker_id})`}
                            value={selectedWorker}
                            onChange={(_, v) => {
                              setRowWorker(row.size, v ? v.id.toString() : '');
                              // Auto-fill qty with full available if not yet typed
                              if (v && !parseInt(row.qty) && cap?.available) {
                                setRowQty(row.size, String(cap.available));
                              }
                            }}
                            renderInput={params => (
                              <TextField
                                {...params}
                                placeholder="Select worker…"
                                size="small"
                                error={!!err && !row.workerId}
                              />
                            )}
                            renderOption={(props, option) => (
                              <li {...props}>
                                <Box>
                                  <Typography variant="body2">{option.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {option.worker_id} · {option.worker_type}
                                  </Typography>
                                </Box>
                              </li>
                            )}
                          />
                          {err && (
                            <Typography variant="caption" color="error" display="block" sx={{ mt: 0.25, px: 0.5 }}>
                              {err}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Qty input */}
                        <TableCell align="center">
                          <TextField
                            size="small"
                            type="number"
                            value={row.qty}
                            onChange={e => setRowQty(row.size, e.target.value)}
                            placeholder="0"
                            error={!!err && parseInt(row.qty) > (cap?.available ?? 0)}
                            inputProps={{
                              min: 0,
                              max: cap?.available ?? 0,
                              style: { textAlign: 'center', padding: '5px 8px' },
                            }}
                            sx={{
                              width: 80,
                              '& input[type=number]': { MozAppearance: 'textfield' },
                              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0,
                              },
                            }}
                          />
                        </TableCell>

                        {/* Clear row */}
                        <TableCell align="center" sx={{ px: 0.5 }}>
                          {(row.workerId || row.qty) && (
                            <IconButton size="small" onClick={() => clearRow(row.size)} sx={{ color: 'text.disabled' }}>
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            {/* Summary footer */}
            {!capacityLoading && filledRows.filter(r => !rowError(r) && parseInt(r.qty) > 0).length > 0 && (
              <Box sx={{
                px: 2, py: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75,
                alignItems: 'center',
              }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Will create:
                </Typography>
                {sizeRows.filter(r => parseInt(r.qty) > 0 && r.workerId && !rowError(r)).map(r => {
                  const worker = availableWorkers.find(w => w.id.toString() === r.workerId);
                  return (
                    <Chip
                      key={r.size}
                      label={`${r.size} → ${worker?.name ?? '?'} (${r.qty} pcs)`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 22 }}
                    />
                  );
                })}
                <Chip
                  label={`${totalPcs} pcs total`}
                  size="small"
                  color="info"
                  sx={{ fontSize: '0.7rem', height: 22, ml: 'auto' }}
                />
              </Box>
            )}
          </Paper>
        )}

        {/* ── Result / error banners ─────────────────────────────────────── */}
        {submitError && (
          <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>
        )}
        {submitResult && submitResult.errors.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              {submitResult.created} assignment(s) created. {submitResult.errors.length} failed:
            </Typography>
            {submitResult.errors.map((e: any, i: number) => (
              <Typography key={i} variant="caption" display="block">
                • {e.size}: {e.error}
              </Typography>
            ))}
            <Button size="small" sx={{ mt: 1 }} onClick={() => { onSaved(); onClose(); }}>
              Close &amp; Refresh
            </Button>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting
            ? 'Creating…'
            : canSubmit
              ? `Create ${filledRows.filter(r => !rowError(r)).length} Assignment${filledRows.filter(r => !rowError(r)).length !== 1 ? 's' : ''} (${totalPcs} pcs)`
              : 'Create Assignments'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
